/* eslint-disable indent */
import CodeEditor from '@uiw/react-textarea-code-editor';
import { Col, Form, Input, Row } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import { FormInstance } from 'antd/lib/form';
import React, { useEffect, useState } from 'react';
import { ClientFeatureConfig } from '../../../types/Feature';
import FeatureStatusInput from '../../Shared/Inputs/FeatureStatusInput';

interface FullEtaFormSectionProps {
    formInstance: FormInstance;
    fullEtaConfig?: ClientFeatureConfig<any>;
    isDetailMode?: boolean;
    enableControls?: boolean;
    resetFormFlag?: boolean;
}

const FullEtaFormSection: React.FC<FullEtaFormSectionProps> = (props: FullEtaFormSectionProps) => {
    const { formInstance, fullEtaConfig, isDetailMode, enableControls, resetFormFlag } = props;

    const [isFeatureDisabled, setIsFeatureDisabled] = useState<boolean>(false);

    useEffect(() => {
        setIsFeatureDisabled(true);
    }, [resetFormFlag]);

    useEffect(() => {
        if (fullEtaConfig) {
            formInstance.setFields([
                { name: 'fullEtaEnabled', value: fullEtaConfig.config.enabled },
                { name: 'errorMargin', value: fullEtaConfig.config.errorMargin },
                { name: 'delta', value: fullEtaConfig.config.delta },
                { name: 'minimumValue', value: fullEtaConfig.config.minimumValue },
                { name: 'shouldCallClosingTool', value: fullEtaConfig.config.shouldCallClosingTool },
            ]);
            setIsFeatureDisabled(!fullEtaConfig.config.enabled);
        }
    }, [fullEtaConfig]);

    return (
        <>
            <div className="card border-0" id="fleets-section">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                    <p className="m-0 p-0 h5" style={{ fontSize: '18px' }}>
                        Full ETA
                    </p>
                    <div className="d-inline-flex align-items-center">
                        <span className="mr-2 d-none d-sm-block">Feature is:</span>
                        <FeatureStatusInput
                            name="fullEtaEnabled"
                            id="fullEtaEnabled"
                            isDetailMode={isDetailMode}
                            enabled={fullEtaConfig?.config.enabled}
                            isFeatureDisabled={isFeatureDisabled}
                            onChange={(value): void => {
                                setIsFeatureDisabled(!value);
                            }}
                            enableControls={enableControls}
                        />
                    </div>
                </div>
                <div className="card-body">
                    <Row gutter={24}>
                        <Col span={24}>
                            <CodeEditor
                                language="js"
                                placeholder="Please enter JS code."
                                padding={0}
                                style={{
                                    fontSize: 12,
                                    backgroundColor: '#f5f5f5',
                                    fontFamily:
                                        'ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace',
                                    marginBottom: 16,
                                }}
                                value={
                                    '//timeToPu = Math.max(poEta, preparation_time);\nconst newPoEta = timeToPu + errorMargin;\nconst low = Math.max(delta, newPoEta - delta / 2 + timePuToDo);\nconst high = Math.max(delta + low, newPoEta + delta / 2 + timePuToDo);\nconst lower_range = low <= minimumValue ? minimumValue : low;\nconst higher_range = low <= minimumValue ? minimumValue + delta : high;'
                                }
                                disabled
                            />
                        </Col>

                        <Col span={6}>
                            <Form.Item
                                name="errorMargin"
                                id="errorMargin"
                                label="Error Margin (in seconds)"
                                rules={
                                    !isDetailMode
                                        ? [
                                              {
                                                  required: !isFeatureDisabled,
                                                  message: 'Must provide errorMargin',
                                              },
                                          ]
                                        : []
                                }
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        {fullEtaConfig?.config.errorMargin || '-'}
                                    </span>
                                ) : (
                                    <Input
                                        className="col-8"
                                        name="errorMargin"
                                        title="Error Margin"
                                        autoComplete="off"
                                        type="number"
                                        disabled={!!isFeatureDisabled || !enableControls}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="delta"
                                id="delta"
                                label="Delta (in seconds)"
                                rules={
                                    !isDetailMode
                                        ? [
                                              {
                                                  required: !isFeatureDisabled,
                                                  message: 'Must provide delta',
                                              },
                                          ]
                                        : []
                                }
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        {fullEtaConfig?.config.delta || '-'}
                                    </span>
                                ) : (
                                    <Input
                                        className="col-8"
                                        name="delta"
                                        title="Delta"
                                        autoComplete="off"
                                        type="number"
                                        disabled={!!isFeatureDisabled || !enableControls}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="minimumValue"
                                id="minimumValue"
                                label="Minimum Value (in seconds)"
                                rules={
                                    !isDetailMode
                                        ? [
                                              {
                                                  required: !isFeatureDisabled,
                                                  message: 'Must provide minimumValue',
                                              },
                                          ]
                                        : []
                                }
                            >
                                {isDetailMode ? (
                                    <span className="ant-form-text text-secondary">
                                        {fullEtaConfig?.config.minimumValue || '-'}
                                    </span>
                                ) : (
                                    <Input
                                        className="col-8"
                                        name="minimumValue"
                                        title="MinimumValue"
                                        autoComplete="off"
                                        type="number"
                                        disabled={!!isFeatureDisabled || !enableControls}
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};

export default FullEtaFormSection;
